/**
 * Streaming API
 * Handles SSE (Server-Sent Events) streaming endpoints
 */

import {
  type SSEEvent,
  type SSEChunkData,
  type SSECompleteData,
  type SSEErrorData,
  type SSEConnectedData,
  type SSEMetadataData,
} from '@/types/sse.types';
import { BACKEND_URL, API_VERSION } from '@/config/api';

/**
 * Configuration for SSE streaming
 */
export interface SSEStreamConfig {
  onConnected?: (data: SSEConnectedData) => void;
  onChunk?: (data: SSEChunkData) => void;
  onComplete?: (data: SSECompleteData) => void;
  onError?: (data: SSEErrorData) => void;
  onMetadata?: (data: SSEMetadataData) => void;
  onAbort?: () => void;
}

/**
 * Create an SSE streaming connection to the example stream endpoint
 *
 * @param config - Event handlers for different SSE events
 * @returns Object with abort function to cancel the stream
 *
 * @example
 * const { abort } = streamExampleText({
 *   onChunk: (data) => handleChunk(data.text),
 *   onComplete: (data) => handleComplete(data),
 * });
 */
export function streamExampleText(config: SSEStreamConfig) {
  const url = `${BACKEND_URL}${API_VERSION}/ai/example-stream`;

  const eventSource = new EventSource(url, {
    withCredentials: false,
  });

  // Handle connection established
  eventSource.addEventListener('connected', (event: MessageEvent) => {
    try {
      const data: SSEConnectedData = JSON.parse(event.data);
      config.onConnected?.(data);
    } catch (error) {
      config.onError?.({
        error: 'parse_error',
        message: 'Failed to parse connected event payload',
      });
    }
  });

  // Handle text chunks
  eventSource.addEventListener('chunk', (event: MessageEvent) => {
    try {
      const data: SSEChunkData = JSON.parse(event.data);
      config.onChunk?.(data);
    } catch (error) {
      config.onError?.({
        error: 'parse_error',
        message: 'Failed to parse chunk event payload',
      });
    }
  });

  // Handle completion
  eventSource.addEventListener('complete', (event: MessageEvent) => {
    try {
      const data: SSECompleteData = JSON.parse(event.data);
      config.onComplete?.(data);
      eventSource.close();
    } catch (error) {
      config.onError?.({
        error: 'parse_error',
        message: 'Failed to parse complete event payload',
      });
    }
  });

  // Handle errors from server
  eventSource.addEventListener('error', (event: MessageEvent) => {
    try {
      const data: SSEErrorData = JSON.parse(event.data);
      config.onError?.(data);
      eventSource.close();
    } catch (error) {
      config.onError?.({
        error: 'parse_error',
        message: 'Failed to parse error event payload',
      });
    }
  });

  // Handle metadata
  eventSource.addEventListener('metadata', (event: MessageEvent) => {
    try {
      const data: SSEMetadataData = JSON.parse(event.data);
      config.onMetadata?.(data);
    } catch (error) {
      config.onError?.({
        error: 'parse_error',
        message: 'Failed to parse metadata event payload',
      });
    }
  });

  // Handle EventSource errors (connection issues)
  eventSource.onerror = () => {
    if (eventSource.readyState === EventSource.CLOSED) {
      config.onError?.({
        error: 'connection_closed',
        message: 'SSE connection was closed',
      });
    } else if (eventSource.readyState === EventSource.CONNECTING) {
      config.onError?.({
        error: 'reconnecting',
        message: 'SSE reconnecting',
      });
    }

    eventSource.close();
  };

  // Return abort function
  return {
    abort: () => {
      eventSource.close();
      config.onAbort?.();
    },
  };
}

/**
 * Helper function to build SSE URL for any endpoint
 * @param endpoint - API endpoint path (without base URL)
 * @returns Full SSE URL
 */
export function buildSSEUrl(endpoint: string): string {
  return `${BACKEND_URL}${API_VERSION}${endpoint}`;
}
